/**
 * Trust Centre administration (policy BUILD-10 §1/§2/§9) — platform admin only.
 *
 * Two registers, both of which publish straight to a page prospects read:
 *   - subprocessors, where creating a FUTURE-dated row mails every confirmed
 *     subscriber; the form says so before you submit, and the result reports how
 *     many were reached and whether the 30-day window was met
 *   - incidents, which are drafted unpublished and only appear publicly once
 *     `published_at` is set
 *
 * The server enforces the platform-admin requirement. This page assumes nothing:
 * a 403 surfaces as an error rather than an empty table, so a non-admin who
 * reaches the URL is told, not silently shown a blank register.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  createIncident,
  createSubprocessor,
  deleteIncident,
  deleteSubprocessor,
  listIncidentsForAdmin,
  listNoticeSubscribers,
  listSubprocessorsForAdmin,
  resendSubprocessorNotice,
  updateIncident,
  type AdminSubscriber,
  type NoticeResult,
  type Subprocessor,
  type TrustIncident,
} from '@/api/trust'

const dateOnly = (value: string | null) => (value ? value.slice(0, 10) : '—')

/** `datetime-local` gives no zone; treat it as the operator's local time. */
function toIso(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

function NoticeSummary({ notice }: { notice: NoticeResult | null }) {
  if (!notice) {
    return (
      <p className="text-sm text-muted-foreground">
        No notice was sent — the start date is not in the future, so this is a record of a processor
        already in place.
      </p>
    )
  }
  return (
    <p className={`text-sm ${notice.short_notice || notice.failed ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
      Notice sent to {notice.sent} of {notice.attempted} subscribers
      {notice.failed ? `, ${notice.failed} failed` : ''}. {notice.days_notice} days'
      notice
      {notice.short_notice ? ' — SHORT of the 30-day commitment.' : '.'}
    </p>
  )
}

export function TrustCentreAdminPage() {
  const [subprocessors, setSubprocessors] = useState<Subprocessor[]>([])
  const [subscribers, setSubscribers] = useState<AdminSubscriber[]>([])
  const [incidents, setIncidents] = useState<TrustIncident[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<NoticeResult | null>(null)
  const [noticeShown, setNoticeShown] = useState(false)

  const [form, setForm] = useState({
    name: '', purpose: '', location: '', data_categories: '', added_at: '',
  })
  const [incidentForm, setIncidentForm] = useState({
    title: '', summary: '', started_at: '', severity: 'minor', status: 'investigating',
  })

  const reload = useCallback(async () => {
    setError(null)
    try {
      const [subs, subscriberList, incidentList] = await Promise.all([
        listSubprocessorsForAdmin(),
        listNoticeSubscribers(),
        listIncidentsForAdmin(),
      ])
      setSubprocessors(subs.subprocessors)
      setSubscribers(subscriberList.subscribers)
      setIncidents(incidentList.incidents)
    } catch (err) {
      setError((err as Error).message || 'Could not load the Trust Centre registers.')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const confirmedCount = subscribers.filter((s) => s.status === 'confirmed').length
  const futureDated = Boolean(form.added_at) && new Date(form.added_at).getTime() > Date.now()

  const submitSubprocessor = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setError(null)
      try {
        const result = await createSubprocessor({
          name: form.name,
          purpose: form.purpose,
          location: form.location,
          data_categories: form.data_categories,
          added_at: toIso(form.added_at),
        })
        setNotice(result.notice)
        setNoticeShown(true)
        setForm({ name: '', purpose: '', location: '', data_categories: '', added_at: '' })
        await reload()
      } catch (err) {
        setError((err as Error).message || 'Could not add the subprocessor.')
      }
    },
    [form, reload],
  )

  const submitIncident = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setError(null)
      try {
        await createIncident({
          title: incidentForm.title,
          summary: incidentForm.summary,
          started_at: toIso(incidentForm.started_at),
          severity: incidentForm.severity as TrustIncident['severity'],
          status: incidentForm.status as TrustIncident['status'],
          // Drafted unpublished. Publishing is a separate, deliberate action.
          published_at: null,
        })
        setIncidentForm({ title: '', summary: '', started_at: '', severity: 'minor', status: 'investigating' })
        await reload()
      } catch (err) {
        setError((err as Error).message || 'Could not add the incident.')
      }
    },
    [incidentForm, reload],
  )

  const field = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
  const labelCls = 'mb-1 block text-sm font-medium text-foreground'

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="mb-2 text-2xl font-bold tracking-tight">Trust Centre</h1>
      <p className="mb-6 text-muted-foreground">
        Everything here publishes to the public Trust Centre. {confirmedCount} confirmed subscriber
        {confirmedCount === 1 ? '' : 's'} will receive advance notices.
      </p>

      {error ? (
        <p role="alert" className="mb-6 rounded-md border border-border p-3 text-sm text-foreground">
          {error}
        </p>
      ) : null}

      {noticeShown ? (
        <div className="mb-6 rounded-md border border-border p-3" role="status" aria-live="polite">
          <NoticeSummary notice={notice} />
        </div>
      ) : null}

      {/* --- Subprocessors ---------------------------------------------- */}
      <section aria-labelledby="sp-heading" className="mb-12">
        <h2 id="sp-heading" className="mb-3 text-lg font-semibold text-foreground">Subprocessors</h2>

        <form onSubmit={submitSubprocessor} className="mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="sp-name">Name</label>
            <input id="sp-name" required className={field} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="sp-purpose">Purpose</label>
            <input id="sp-purpose" required className={field} value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="sp-location">Processing location</label>
            <input id="sp-location" required className={field} value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="sp-categories">Data categories</label>
            <input id="sp-categories" className={field} placeholder="name, email address, phone number"
              value={form.data_categories}
              onChange={(e) => setForm({ ...form, data_categories: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="sp-added">Active from</label>
            <input id="sp-added" type="datetime-local" required className={field} value={form.added_at}
              onChange={(e) => setForm({ ...form, added_at: e.target.value })}
              aria-describedby="sp-added-help" />
            {/* The consequence is stated before the click, not after it. */}
            <p id="sp-added-help" className="mt-1 text-xs text-muted-foreground">
              {futureDated
                ? `A future date sends the advance notice immediately to all ${confirmedCount} confirmed subscribers.`
                : 'A past date records a processor already in place and sends no notice.'}
            </p>
          </div>
          <div className="flex items-end">
            <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
              Add subprocessor
            </button>
          </div>
        </form>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">All subprocessors, including removed ones.</caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Name</th>
                <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Location</th>
                <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Active from</th>
                <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Removed</th>
                <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Notice sent</th>
                <th scope="col" className="py-2 font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.length === 0 ? (
                <tr><td colSpan={6} className="py-3 text-muted-foreground">No subprocessors recorded.</td></tr>
              ) : null}
              {subprocessors.map((row) => (
                <tr key={row.id} className="border-b border-border/60">
                  <td className="py-2 pr-4 font-medium text-foreground">{row.name}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{row.location}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{dateOnly(row.added_at)}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{dateOnly(row.removed_at)}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{dateOnly(row.notice_sent_at)}</td>
                  <td className="py-2">
                    <button type="button" className="mr-3 underline underline-offset-4"
                      onClick={async () => {
                        try {
                          const res = await resendSubprocessorNotice(row.id)
                          setNotice(res.notice)
                          setNoticeShown(true)
                          await reload()
                        } catch (err) {
                          setError((err as Error).message)
                        }
                      }}>
                      Re-send notice
                    </button>
                    <button type="button" className="underline underline-offset-4"
                      onClick={async () => {
                        try {
                          await deleteSubprocessor(row.id)
                          await reload()
                        } catch (err) {
                          setError((err as Error).message)
                        }
                      }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* --- Incidents --------------------------------------------------- */}
      <section aria-labelledby="inc-heading">
        <h2 id="inc-heading" className="mb-3 text-lg font-semibold text-foreground">Incident history</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          New incidents are drafted unpublished. Publishing is a separate action.
        </p>

        <form onSubmit={submitIncident} className="mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="inc-title">Title</label>
            <input id="inc-title" required className={field} value={incidentForm.title}
              onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="inc-started">Started at</label>
            <input id="inc-started" type="datetime-local" required className={field}
              value={incidentForm.started_at}
              onChange={(e) => setIncidentForm({ ...incidentForm, started_at: e.target.value })} />
          </div>
          <div>
            <label className={labelCls} htmlFor="inc-severity">Severity</label>
            <select id="inc-severity" className={field} value={incidentForm.severity}
              onChange={(e) => setIncidentForm({ ...incidentForm, severity: e.target.value })}>
              <option value="minor">minor</option>
              <option value="major">major</option>
              <option value="critical">critical</option>
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="inc-status">Status</label>
            <select id="inc-status" className={field} value={incidentForm.status}
              onChange={(e) => setIncidentForm({ ...incidentForm, status: e.target.value })}>
              <option value="investigating">investigating</option>
              <option value="identified">identified</option>
              <option value="monitoring">monitoring</option>
              <option value="resolved">resolved</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="inc-summary">Summary</label>
            <textarea id="inc-summary" required rows={3} className={field} value={incidentForm.summary}
              onChange={(e) => setIncidentForm({ ...incidentForm, summary: e.target.value })} />
          </div>
          <div>
            <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
              Add incident
            </button>
          </div>
        </form>

        <ul className="space-y-3">
          {incidents.length === 0 ? (
            <li className="text-muted-foreground">No incidents recorded.</li>
          ) : null}
          {incidents.map((incident) => (
            <li key={incident.id} className="border-b border-border/60 pb-3">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-medium text-foreground">{incident.title}</span>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {incident.severity} · {incident.status} ·{' '}
                  {incident.published_at ? `published ${dateOnly(incident.published_at)}` : 'draft'}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{incident.summary}</p>
              <div className="mt-2 text-sm">
                <button type="button" className="mr-3 underline underline-offset-4"
                  onClick={async () => {
                    try {
                      await updateIncident(incident.id, {
                        published_at: incident.published_at ? null : new Date().toISOString(),
                      })
                      await reload()
                    } catch (err) {
                      setError((err as Error).message)
                    }
                  }}>
                  {incident.published_at ? 'Unpublish' : 'Publish'}
                </button>
                <button type="button" className="underline underline-offset-4"
                  onClick={async () => {
                    try {
                      await deleteIncident(incident.id)
                      await reload()
                    } catch (err) {
                      setError((err as Error).message)
                    }
                  }}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

export default TrustCentreAdminPage
